/**
 * Summary Card Component
 * Individual stat card with colored top border, icon, value, and secondary info
 */

import type { LucideIcon } from "lucide-react";
import { Info, MoveUp } from "lucide-react";
import { ZTooltip } from "@/shared/components/ZTooltip/ZTooltip";

export type CardAccentColor = "blue" | "green" | "red" | "yellow";

interface SecondaryInfo {
  label: string;
  value: string;
  showTrend?: boolean;
  trendDirection?: "up" | "down";
}

export interface SummaryCardProps {
  title: string;
  value: string | number;
  secondaryValue?: string;
  icon: LucideIcon;
  accentColor: CardAccentColor;
  tooltip?: string;
  secondaryInfo?: SecondaryInfo | SecondaryInfo[];
  onClick?: () => void;
  isClickable?: boolean;
}

const accentColors: Record<
  CardAccentColor,
  {
    border: string;
    iconBg: string;
    iconColor: string;
    trendColor: string;
  }
> = {
  blue: {
    border: "border-t-[#006FEE]",
    iconBg: "bg-[#E6F1FE]",
    iconColor: "text-[#006FEE]",
    trendColor: "text-[#17C964]",
  },
  green: {
    border: "border-t-[#17C964]",
    iconBg: "bg-[#E8FAF0]",
    iconColor: "text-[#17C964]",
    trendColor: "text-[#17C964]",
  },
  red: {
    border: "border-t-[#F31260]",
    iconBg: "bg-[#FEE7EF]",
    iconColor: "text-[#F31260]",
    trendColor: "text-[#F31260]",
  },
  yellow: {
    border: "border-t-[#F5A524]",
    iconBg: "bg-[#FEFCE8]",
    iconColor: "text-[#F5A524]",
    trendColor: "text-[#90A1B9]",
  },
};

export const SummaryCard = ({
  title,
  value,
  secondaryValue,
  icon: Icon,
  accentColor,
  tooltip,
  secondaryInfo,
  onClick,
  isClickable = false,
}: SummaryCardProps) => {
  const colors = accentColors[accentColor];
  const secondaryInfoArray = secondaryInfo ? (Array.isArray(secondaryInfo) ? secondaryInfo : [secondaryInfo]) : [];

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (isClickable && onClick && (e.key === "Enter" || e.key === " ")) {
      e.preventDefault();
      onClick();
    }
  };

  return (
    <div
      className={`flex flex-col border-t-2 ${colors.border} rounded-lg shadow-[0px_1px_3px_rgba(0,0,0,0.1),0px_1px_2px_rgba(0,0,0,0.06)] ${
        isClickable ? "cursor-pointer hover:shadow-lg transition-shadow" : ""
      }`}
      onClick={isClickable ? onClick : undefined}
      onKeyDown={handleKeyDown}
      role={isClickable ? "button" : undefined}
      tabIndex={isClickable ? 0 : undefined}
    >
      <div className="flex flex-col justify-between p-4 gap-4 bg-white rounded-lg h-[148px]">
        {/* Top Section: Title, Value, Icon */}
        <div className="flex flex-row items-start">
          {/* Left: Title and Value */}
          <div className="flex flex-col gap-1 flex-1">
            {/* Title with optional tooltip */}
            <div className="flex flex-row items-start gap-1">
              <span className="font-inter font-medium text-xs leading-5 text-[#62748E]">{title}</span>
              {tooltip && (
                <ZTooltip content={tooltip} isDisabled={false}>
                  <button type="button" className="p-0.5">
                    <Info size={16} className="text-[#90A1B9]" />
                  </button>
                </ZTooltip>
              )}
            </div>

            {/* Value */}
            <div className="flex flex-row items-baseline gap-2">
              <span className="font-inter font-semibold text-xl leading-7 text-[#0F172B]">{value}</span>
              {secondaryValue && (
                <span className="font-inter font-medium text-base leading-6 text-[#90A1B9]">{secondaryValue}</span>
              )}
            </div>
          </div>

          {/* Right: Icon */}
          <div className={`flex items-center justify-center w-12 h-12 ${colors.iconBg} rounded-lg`}>
            <Icon size={24} className={colors.iconColor} />
          </div>
        </div>

        {/* Bottom Section: Secondary Info */}
        {secondaryInfoArray.length > 0 && (
          <div className={`flex flex-row items-start gap-4`}>
            {secondaryInfoArray.map((info, index) => (
              <div key={index} className="flex flex-col gap-1 flex-1">
                <span className="font-inter font-medium text-xs leading-5 text-[#62748E]">{info.label}</span>
                <div className="flex flex-row items-center gap-1.5">
                  {info.showTrend && (
                    <MoveUp
                      size={12}
                      className={info.trendDirection === "down" ? "rotate-180 " + colors.trendColor : colors.trendColor}
                    />
                  )}
                  <span
                    className={`font-inter font-normal text-xs leading-4 ${
                      info.showTrend ? colors.trendColor : "text-[#90A1B9]"
                    }`}
                  >
                    {info.value}
                  </span>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default SummaryCard;
